/**
 * Scanline generation.
 *
 * A `repeating-linear-gradient` in CSS pixels is wrong on high-DPI screens: a
 * 1px/3px period becomes 9 device pixels at DPR 3 — three times coarser than
 * intended — and shimmers on the non-integer scale factors our fixed 1080x1920
 * canvas always produces (iPhone 15 Pro is 1179x2556, many Androids 1440x3120).
 *
 * Instead: generate a 1 x 4 DEVICE-pixel tile and let background-size divide by
 * DPR, so the period lands on real pixels. Regenerated when DPR changes, which
 * happens when a window moves between displays.
 */

const PERIOD_DEVICE_PX = 4;

function tile(alpha: number): string {
  const c = document.createElement('canvas');
  c.width = 1;
  c.height = PERIOD_DEVICE_PX;
  const g = c.getContext('2d');
  if (!g) return 'none';
  g.fillStyle = `rgba(0,0,0,${alpha})`;
  g.fillRect(0, 0, 1, 1);
  return `url(${c.toDataURL()})`;
}

export function installScanlines(): () => void {
  const root = document.documentElement;

  const update = () => {
    const dpr = window.devicePixelRatio || 1;
    root.style.setProperty('--dpr', String(dpr));
    root.style.setProperty('--scanline-size', `${PERIOD_DEVICE_PX / dpr}px`);
    // Two intensities: the shell is lighter than the diegetic pass because it
    // sits over UI chrome and commerce text, where contrast is inviolable.
    root.style.setProperty('--scanline-diegetic', tile(0.08));
    root.style.setProperty('--scanline-shell', tile(0.05));
  };

  update();

  // DPR changes fire as a media-query change, not a resize.
  let mq = matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
  const onChange = () => {
    update();
    mq.removeEventListener('change', onChange);
    mq = matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    mq.addEventListener('change', onChange);
  };
  mq.addEventListener('change', onChange);

  return () => mq.removeEventListener('change', onChange);
}
